<template>
  <section class="section-base form-section-base">
    <div class="form-section-base__title">Параметры запроса</div>
    <v-container>
      <form class="form-base">
        <v-row>
          <v-col>
            <v-radio-group v-model="radioGroup" column>
              <v-radio
                label="Получение информации по номеру уведомления о залоге движимого имущества"
                :value="1"
                class="pb-3"
              />

              <!-- radioGroup - 1: start -->
              <div v-if="radioGroup === 1" class="form-base--inner">
                <v-container>
                  <v-row>
                    <v-col>
                      <v-text-field
                        v-model="$v.creationReferenceNumber.$model"
                        :errorMessages="
                          getValidationErrorText($v.creationReferenceNumber)
                        "
                        v-mask="['####-###-######-###']"
                        label="Регистрационный номер уведомления о залоге движимого имущества"
                        outlined
                      ></v-text-field>
                    </v-col>
                  </v-row>
                </v-container>
              </div>
              <!-- radioGroup - 1: end -->

              <v-radio
                label="Получение информации обо всех уведомлениях о залоге движимого имущества в отношении определенного залогодателя"
                :value="2"
                class="pb-3"
              />

              <!-- radioGroup - 2: start -->
              <div v-if="radioGroup === 2" class="form-base--inner">
                <v-container>
                  <v-row>
                    <v-col class="pt-0">
                      <v-radio-group v-model="role" column class="mt-0">
                        <v-radio
                          label="российское юридическое лицо"
                          :value="Role.ORGANIZATION"
                        />
                        <div
                          v-if="role === Role.ORGANIZATION"
                          class="form-base--inner"
                        >
                          <Organization :v="$v.organization" />
                        </div>

                        <v-radio
                          label="иностранное юридическое лицо или международная организация, имеющая права юридического лица"
                          :value="Role.FOREIGN_ORGANIZATION"
                        />
                        <div
                          v-if="role === Role.FOREIGN_ORGANIZATION"
                          class="form-base--inner"
                        >
                          <ForeignOrganization :v="$v.foreignOrganization" />
                        </div>

                        <v-radio
                          label="индивидуальный предприниматель"
                          :value="Role.BUSINESSMAN"
                        />
                        <div
                          v-if="role === Role.BUSINESSMAN"
                          class="form-base--inner"
                        >
                          <Businessman :v="$v.businessman" :regions="regions" />
                        </div>

                        <v-radio label="физическое лицо" :value="Role.PERSON" />
                        <div
                          v-if="role === Role.PERSON"
                          class="form-base--inner"
                        >
                          <Person :v="$v.person" :regions="regions" />
                        </div>
                      </v-radio-group>
                    </v-col>
                  </v-row>
                </v-container>
              </div>
              <!-- radioGroup - 2: end -->
            </v-radio-group>
          </v-col>
        </v-row>
      </form>

      <Footer
        @next-btn-click="btnSaveClickHandler(StepDirection.NEXT)"
        @prev-btn-click="btnSaveClickHandler(StepDirection.PREV)"
        :loading-next="loadingNext"
      />
    </v-container>
  </section>
</template>

<script>
import { minLength, required } from 'vuelidate/lib/validators';
import cloneDeep from 'clone-deep';
import deepmerge from 'deepmerge';

import { checkInvalid, leaveStep } from '@/utils/services';
import { getValidationErrorText } from '@/utils/validate';
import { Role } from '@/utils/dictionaries';
import { ServicesModule } from '@/store/modules/services';
import Footer from '@/components/services/elements/Footer';
import { StepDirection, StepName } from '@/types/services';
import Organization, {
  organizationModel,
  organizationValidation,
} from './Organization';
import ForeignOrganization, {
  foreignOrganizationModel,
  foreignOrganizationValidation,
} from './ForeignOrganization';
import Businessman, {
  businessmanModel,
  businessmanValidation,
} from './Businessman';
import Person, { personModel, personValidation } from './Person';
import { RegionModule } from '@/store/modules/region';
import { RuzdiStep } from '@/constants';

export default {
  name: 'Pledgor',
  props: {
    loadingNext: {
      type: Boolean,
      required: true,
    },
  },
  components: {
    ForeignOrganization,
    Organization,
    Businessman,
    Person,
    Footer,
  },
  data: () => ({
    StepDirection,
    StepName,
    leaveStep,
    Role,
    getValidationErrorText,
    invalidFields: [],
    radioGroup: 1,
    role: Role.ORGANIZATION,
    creationReferenceNumber: '',
    organization: cloneDeep(organizationModel),
    foreignOrganization: cloneDeep(foreignOrganizationModel),
    person: cloneDeep(personModel),
    businessman: cloneDeep(businessmanModel),
  }),
  validations() {
    return {
      radioGroup: {},
      role: {},
      creationReferenceNumber: {
        required,
        minLength: minLength(19),
      },
      organization: cloneDeep(organizationValidation),
      foreignOrganization: cloneDeep(foreignOrganizationValidation),
      businessman: cloneDeep(businessmanValidation),
      person: cloneDeep(personValidation),
    };
  },
  watch: {
    role(value) {
      this.person.isBusinessman = value === Role.BUSINESSMAN;
    },
  },
  computed: {
    regions() {
      return RegionModule.list;
    },
  },
  async mounted() {
    if (!RegionModule.loading && !RegionModule.loaded) {
      await RegionModule.loadRegionsAll();
    }
  },
  created() {
    this.prefillDraft();
  },
  methods: {
    prefillDraft() {
      if (!ServicesModule.statement) return;

      const statementStore = cloneDeep(ServicesModule.statement);

      if (statementStore.ruzdiDetails) {
        this.radioGroup = 1;
        this.creationReferenceNumber =
          statementStore.ruzdiDetails.creationReferenceNumber;
      }

      if (!ServicesModule.statement.subjects.pledgor) return;

      this.radioGroup = 2;

      const pledgor = statementStore.subjects.pledgor;

      if (pledgor.foreignOrganization) {
        this.role = Role.FOREIGN_ORGANIZATION;
        this.foreignOrganization = deepmerge(
          foreignOrganizationModel,
          pledgor.foreignOrganization
        );
      }

      if (pledgor.person) {
        if (pledgor.person.isBusinessman) {
          this.role = Role.BUSINESSMAN;
          this.businessman = deepmerge(businessmanModel, pledgor.person);

          if (pledgor.identityDocument) {
            this.businessman.identityDocument = deepmerge(
              businessmanModel.identityDocument,
              pledgor.identityDocument
            );
          }
        } else {
          this.role = Role.PERSON;
          this.person = deepmerge(personModel, pledgor.person);

          if (pledgor.identityDocument) {
            this.person.identityDocument = deepmerge(
              personModel.identityDocument,
              pledgor.identityDocument
            );
          }
        }
      }

      if (pledgor.organization) {
        this.role = Role.ORGANIZATION;
        this.organization = deepmerge(organizationModel, pledgor.organization);
      }
    },

    touchFields() {
      this.$v.$reset();
      this.invalidFields = [];

      if (this.radioGroup === 1) {
        this.$v.creationReferenceNumber.$touch();
        checkInvalid(this.$v.creationReferenceNumber, this.invalidFields);
      } else {
        this.$v[this.role].$touch();
        checkInvalid(this.$v[this.role], this.invalidFields);
      }
    },

    assemblyModel() {
      const model = {
        ruzdiDetails: {
          creationReferenceNumber: null,
        },
        pledgor: null,
      };

      if (this.radioGroup === 1) {
        model.ruzdiDetails.creationReferenceNumber = this.creationReferenceNumber;
      } else {
        model.pledgor = {};

        if ([Role.BUSINESSMAN, Role.PERSON].includes(this.role)) {
          model.pledgor.person = cloneDeep(this[this.role]);
          delete model.pledgor.person.identityDocument;

          model.pledgor.identityDocument = cloneDeep(
            this[this.role].identityDocument
          );
        } else {
          model.pledgor[this.role] = cloneDeep(this[this.role]);
        }
      }

      return model;
    },

    btnSaveClickHandler(direction) {
      leaveStep({
        stepThis: this,
        direction,
        step: RuzdiStep.PLEDGOR,
      });
    },
  },
};
</script>
